import { Alert, Box, Stack, Grid, Container } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import dayjs from "dayjs";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import GeneralStatusLabel from "../../components/Common/GeneralStatusLabel";
import Notification from "../../components/Common/Notification";
import FormPage from "../../components/Form/FormPage";
import FormSection from "../../components/Form/FormSection";
import FormActionBar from "../../components/Form/FormActionBar";
import CustomTextField from "../../components/Form/CustomTextField";
import CustomSelect from "../../components/Form/CustomSelect";
import DatePicker from "../../components/Form/DatePicker";
import LoadingDialog from "../../components/Form/LoadingDialog";
import Helpers from "../../helpers/helpers";
import { useCrud } from "../../store/crud";
import bookingsModule from "../../store/packageBookings/module";
import {
  appointmentDate, appointmentTime, assignedEmail, bookingId, packageName, packageVariant,
  patientName,
} from "./bookingPresentation";
import { bookingActionTypes, bookingDialogPayload, newBookingDialogValues } from "./bookingDialogModel";
import PackageDialog from "./PackageDialog";
import "./package-bookings.css";
const statuses = {
  pending: {
    color: "warning",
    title: "Pending confirmation.",
    text: "Review and confirm or reject this booking.",
    actions: [["cancelled", "Cancel Booking"], ["confirmed", "Confirm & Notify"]]
  },
  confirmed: {
    color: "info",
    title: "Booking confirmed.",
    text: "Patient notified. Reschedule or cancel if needed.",
    actions: [["cancelled", "Cancel booking"], ["reschedule", "Reschedule"], ["completed", "Mark as completed"]]
  },
  cancelled: {
    color: "secondary",
    title: "Booking cancelled.",
    text: "This appointment has been cancelled.",
    actions: [["pending", "Re-open booking"]]
  },
  completed: {
    color: "success",
    title: "Appointment completed.",
    text: "This booking is closed.",
    actions: []
  }
};
const historyActionLabels = {
  create: "Booking created",
  confirm: "Booking confirmed",
  cancel: "Booking cancelled",
  reopen: "Booking reopened",
  reschedule: "Booking rescheduled",
  complete: "Booking completed",
  update: "Booking updated",
};
const readableHistoryValue = value => String(value || "").replace(/_/g, " ").replace(/^./, letter => letter.toUpperCase());
function InfoRows({ rows }) {
  return <dl className="mb-0">
      {rows.map(([label, value]) => <Grid
      key={label} container
      sx={{
        mb: 2
      }}
    >
      <Grid xs={5} sm={3} className="text-muted" item>
        <dt className="fw-normal">{label}</dt>
      </Grid>
      <Grid xs={7} sm={9} item>
        <dd className="fw-semibold mb-0 text-break">{value || "-"}</dd>
      </Grid>
    </Grid>)}
    </dl>;
}
export default function BookingDetails() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { details, manage, reset, state, loading, notification, setNotification } = useCrud(bookingsModule);
  const [values, setValues] = useState({
    assigned_department_email: "",
    assigned_admin_id: "",
    follow_up_at: ""
  });
  const [dialogValues, setDialogValues] = useState(newBookingDialogValues);
  const [dialog, setDialog] = useState({
    open: false
  });
  const permissions = useSelector(root => root.Login.permissionArry) || [];
  const admins = useSelector(root => root.General.admins) || [];
  const currentAdmin = useSelector(root => root.Login.verifyAdmin?.user?.data) || {};
  const canEdit = permissions.includes("edit-package");
  const source = location.state?.bookingsReturnTo || "";
  const returnTo = /^\/packages\/bookings(?:\?|$)/.test(source) ? source : "/packages/bookings";
  useEffect(() => {
    reset("details");
    details({ id });
  }, [id, details, reset]);
  const booking = state.details.status === "success" ? state.details.data : null;
  useEffect(() => {
    if (!booking) return;
    const assignedAdmin = booking.assignee || booking.assigned_admin || {};
    setValues({
      assigned_department_email: booking.assigned_department_email
        || (assignedEmail(booking) === "-" ? "" : assignedEmail(booking)),
      assigned_admin_id: booking.assigned_admin_id ?? assignedAdmin.id ?? currentAdmin.id ?? "2",
      follow_up_at: booking.follow_up_at && dayjs(booking.follow_up_at).isValid()
        ? dayjs(booking.follow_up_at).format("YYYY-MM-DD") : booking.follow_up_date || ""
    });
  }, [booking, currentAdmin.id]);
  const status = String(booking?.status || "").toLowerCase();
  const config = statuses[status];
  const locked = !canEdit || status === "completed";
  const change = key => event => {
    setValues(current => ({
      ...current,
      [key]: event.target.value
    }));
  };
  const openAction = action => {
    setDialogValues(newBookingDialogValues());
    setDialog({
      open: true,
      id,
      type: bookingActionTypes[action]
    });
  };
  const submit = (bookingID, formValues) => {
    manage({
      id: bookingID,
      action: dialog.type,
      data: bookingDialogPayload(dialog.type, formValues, values)
    }, {
      successMessage: dialog.type === "reschedule" ? "Booking rescheduled" : "Booking updated",
      onSuccess: () => {
        setDialog({
          open: false
        });
        details({
          id
        }, {
          preserveNotification: true
        });
      }
    });
  };
  const back = () => navigate(returnTo, {
    state: location.state?.bookingsListState
  });
  const assignedAdmin = booking?.assignee || booking?.assigned_admin || {};
  const picOptions = admins
  const history = Array.isArray(booking?.history) ? booking.history : [];
  return <FormPage>
    <Container
      maxWidth={false} disableGutters
      sx={{
        px: 1.5,
        pb: 3
      }}
    >
      <Breadcrumbs
        title="Booking Details"
        breadcrumbItems={[{
            title: "CVSKL",
            link: "/dashboard"
          }, {
            title: "Packages",
            link: "/packages"
          }, {
            title: "Package Bookings",
            link: returnTo
          }, {
            title: "Edit Booking",
            link: "#"
        }]}
      />
      <Notification notification={notification} setNotification={setNotification} />
      <LoadingDialog open={loading} />
      <PackageDialog
        content={dialog} setContent={setDialog} data={booking || {}} values={dialogValues}
        setValues={setDialogValues} onConfirm={submit} loading={state.manage.loading}
      />
        {booking && <>
          {config && <Alert
            severity={status === "cancelled" ? "info" : config.color}
            icon={<i className="ri-information-line" />}
            sx={{ mb: 2, ...(status === "cancelled" ? { bgcolor: "#edf2f5", color: "#14242f" } : {}) }}
          >
        <span>
          <strong>{config.title}</strong> {config.text}</span>
      </Alert>}
      <Box component="section" className="admin-form-section" mb={2}>
        <h5 className="mb-2">{bookingId(booking)}</h5>
        <Box className="text-muted">
              {patientName(booking)} <span className="mx-2">|</span> {packageName(booking)}
              {packageVariant(booking) !== "-" && ` (${packageVariant(booking)})`}
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <Stack spacing={3}>
            <FormSection title="Package Booking Info">
              <InfoRows
                rows={[
                  ["Package", packageName(booking)],
                  ["Variation", packageVariant(booking)],
                  ["Appointment Date", appointmentDate(booking)],
                  ["Appointment Time", appointmentTime(booking)],
                  ["Booked Price", booking.booked_price != null ? `${booking.currency || "MYR"} ${Number(booking.booked_price).toFixed(2)}` : "-"],
                  ["Quantity", booking.quantity ?? "-"],
                  ["Booking Status", <GeneralStatusLabel label={status} list={Helpers.bookingStatuses} />],
                  ["Notes", booking.notes],
                ]}
              />
            </FormSection>
            <FormSection title="Patient Information">
              <InfoRows
                rows={[
                  ["Patient Name", patientName(booking)],
                  ["NRIC / Passport", booking.member?.identity_number || booking.patient?.nric || booking.patient?.passport
                    || booking.patient_nric || booking.nric],
                  ["Email Address", booking.member?.email || booking.patient?.email || booking.patient_email || booking.email],
                  ["Phone Number", booking.member?.phone || booking.patient?.phone || booking.patient_phone || booking.phone],
                ]}
              />
            </FormSection>
            <FormSection title="Audit Trail">
                  {history.length ? history.map((entry, index, entries) => {
                    const administrator = entry.performed_by_admin;
                    const changes = entry.changes || {};
                    const fromStatus = entry.from_status || changes.status?.from;
                    const toStatus = entry.to_status || changes.status?.to;
                    return (
                    <Box
                      key={entry.id || index}
                      sx={index < entries.length - 1 ? { borderBottom: 1, borderColor: "divider", pb: 2, mb: 2 } : {}}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} mb={1}>
                        <Box component="strong">
                          {historyActionLabels[entry.action] || readableHistoryValue(entry.action) || "Booking activity"}
                        </Box>
                        <Box className="text-muted">
                          {entry.created_at ? dayjs(entry.created_at).format("DD MMM YYYY, h:mm A") : "-"}
                        </Box>
                      </Stack>
                      {(fromStatus || toStatus) && <Box mb={0.5}>
                        <span className="text-muted">Status: </span>
                        {fromStatus && toStatus
                          ? <>{readableHistoryValue(fromStatus)} &rarr; {readableHistoryValue(toStatus)}</>
                          : readableHistoryValue(toStatus || fromStatus)}
                      </Box>}
                      {administrator && <Box mb={0.5}>
                        <span className="text-muted">Performed by: </span>
                        {administrator.name || administrator.email || "-"}
                        {administrator.name && administrator.email ? ` (${administrator.email})` : ""}
                      </Box>}
                      {changes.reason_code && <Box mb={0.5}>
                        <span className="text-muted">Reason: </span>{readableHistoryValue(changes.reason_code)}
                      </Box>}
                      {changes.internal_note && <Box>
                        <span className="text-muted">Internal note: </span>{changes.internal_note}
                      </Box>}
                    </Box>
                    );
                  }) : <Box className="text-muted">No activity recorded.</Box>}
            </FormSection>
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormSection title="Admin Management" className="booking-admin-management">
            <Stack spacing={3}>
              <CustomTextField
                title="Assigned Department Email" values={values.assigned_department_email}
                onChange={change("assigned_department_email")} disabled={locked}
              />
              <CustomSelect
                title="Assigned PIC" values={values.assigned_admin_id} data={picOptions}
                onChange={change("assigned_admin_id")} disabled={locked}
              />
              <DatePicker
                title="Follow-up Date" name="follow_up_at" values={values.follow_up_at}
                setFormValues={setValues} disabled={locked}
              />
            </Stack>
          </FormSection>
        </Grid>
      </Grid>
      <FormActionBar>
            {canEdit && config?.actions.map(([action, label], index) => <Button
          key={action} disabled={loading} onClick={() => openAction(action)}
          color={config.actions.length > 1 && index === config.actions.length - 1 ? "primary-button" : "light"}
        >
                {label}
        </Button>)}
            {(status === "completed" || !canEdit || !config) && <Button color="light" onClick={back}>Back to booking</Button>}
      </FormActionBar>
        </>}
        {!booking && !loading && <Box className="admin-form-section">
        <p>Booking details are unavailable.</p>
        <Button color="light" onClick={back}>Back to booking</Button>
      </Box>}
    </Container>
    </FormPage>;
}
